import { useState, useEffect } from "react";
import { useAuth, apiClient } from "@/App";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { 
  Building2, 
  Plus, 
  Phone, 
  Mail,
  MapPin,
  Star,
  Users,
  Clock
} from "lucide-react";
import { toast } from "sonner";

export default function ClinicsPage() {
  const { user } = useAuth();
  const [clinics, setClinics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newClinic, setNewClinic] = useState({
    name: "",
    address: "",
    city: "",
    phone: "",
    email: "",
    timezone: "Asia/Kolkata",
    services: [],
    rating: null
  });

  useEffect(() => {
    fetchClinics();
  }, []);

  const fetchClinics = async () => {
    try {
      const response = await apiClient.get("/clinics");
      setClinics(response.data);
    } catch (error) {
      console.error("Failed to fetch clinics:", error);
      toast.error("Failed to load clinics");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateClinic = async (e) => {
    e.preventDefault();
    try {
      const response = await apiClient.post("/clinics", newClinic);
      setClinics([response.data, ...clinics]);
      toast.success("Clinic created successfully!");
      setDialogOpen(false);
      setNewClinic({
        name: "",
        address: "",
        city: "",
        phone: "",
        email: "",
        timezone: "Asia/Kolkata",
        services: [],
        rating: null
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create clinic");
    }
  };

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8 space-y-6" data-testid="clinics-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-['Manrope']">Clinics</h1>
            <p className="text-slate-500">Manage HearClear clinic network</p>
          </div>
          
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-sky-600 hover:bg-sky-700" data-testid="add-clinic-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Clinic
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Add New Clinic</DialogTitle>
                <DialogDescription>Create a new clinic location</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreateClinic} className="space-y-4 mt-4">
                <div>
                  <Label>Clinic Name *</Label>
                  <Input
                    value={newClinic.name}
                    onChange={(e) => setNewClinic({...newClinic, name: e.target.value})}
                    placeholder="HearClear Location Name"
                    required
                  />
                </div>
                <div>
                  <Label>Address *</Label>
                  <Input
                    value={newClinic.address}
                    onChange={(e) => setNewClinic({...newClinic, address: e.target.value})}
                    placeholder="Full address"
                    required
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>City *</Label>
                    <Input
                      value={newClinic.city}
                      onChange={(e) => setNewClinic({...newClinic, city: e.target.value})}
                      placeholder="City"
                      required
                    />
                  </div>
                  <div>
                    <Label>Phone *</Label>
                    <Input
                      value={newClinic.phone}
                      onChange={(e) => setNewClinic({...newClinic, phone: e.target.value})}
                      placeholder="+91-9888438843"
                      required
                    />
                  </div>
                </div>
                <div>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={newClinic.email}
                    onChange={(e) => setNewClinic({...newClinic, email: e.target.value})}
                    placeholder="clinic@hearclear.in"
                  />
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-sky-600 hover:bg-sky-700">
                    Create Clinic
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 bg-sky-100 rounded-xl flex items-center justify-center">
                <Building2 className="w-6 h-6 text-sky-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{clinics.length}</p>
                <p className="text-sm text-slate-500">Total Clinics</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                <Star className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">
                  {clinics.length > 0 
                    ? (clinics.reduce((acc, c) => acc + (c.rating || 0), 0) / clinics.filter(c => c.rating).length).toFixed(1)
                    : "N/A"
                  }
                </p>
                <p className="text-sm text-slate-500">Avg. Rating</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                <MapPin className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">
                  {new Set(clinics.map(c => c.city)).size}
                </p>
                <p className="text-sm text-slate-500">Cities Covered</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Clinics Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {loading ? (
            [1, 2, 3, 4, 5, 6].map(i => (
              <div key={i} className="skeleton h-48 rounded-xl"></div>
            ))
          ) : clinics.length > 0 ? (
            clinics.map(clinic => (
              <Card key={clinic.id} className="card-hover" data-testid={`clinic-card-${clinic.id}`}>
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-3">
                    <div className="w-10 h-10 bg-sky-100 rounded-xl flex items-center justify-center">
                      <Building2 className="w-5 h-5 text-sky-600" />
                    </div>
                    {clinic.rating && (
                      <Badge variant="outline" className="flex items-center gap-1">
                        <Star className="w-3 h-3 text-amber-500 fill-amber-500" />
                        {clinic.rating}
                      </Badge>
                    )}
                  </div>
                  
                  <h3 className="font-semibold text-slate-900 mb-1">{clinic.name}</h3>
                  <p className="text-sm text-slate-500 mb-4">{clinic.city}</p>
                  
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2 text-slate-600">
                      <MapPin className="w-4 h-4 text-slate-400" />
                      <span className="truncate">{clinic.address}</span>
                    </div>
                    <div className="flex items-center gap-2 text-slate-600">
                      <Phone className="w-4 h-4 text-slate-400" />
                      <span>{clinic.phone}</span>
                    </div>
                    {clinic.email && (
                      <div className="flex items-center gap-2 text-slate-600">
                        <Mail className="w-4 h-4 text-slate-400" />
                        <span className="truncate">{clinic.email}</span>
                      </div>
                    )}
                  </div>

                  {clinic.services?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-4">
                      {clinic.services.slice(0, 3).map((service, idx) => (
                        <Badge key={idx} variant="secondary" className="text-xs">
                          {service}
                        </Badge>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))
          ) : (
            <Card className="col-span-full">
              <CardContent className="py-12 text-center">
                <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">No clinics found</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
